import React, {FC, useEffect, useMemo} from 'react';

import loadable from '@loadable/component';
import {connect} from 'react-redux';

import {useFeatureWithAnalytics} from 'components/feature';
import {PURCHASE_STORAGE} from 'constants/analytics';
import {ApplicationStoreType} from 'desktop/store';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';
import {sessionStore} from 'libs/storage/storage';
import {MessageType} from 'types/cartType';

import {CartSkeleton} from './components/cartSkeleton/cartSkeleton';
import {useCartDeepLink} from './hooks/useCartDeepLink/useCartDeepLink';
import {useHoox} from './hooks/useHoox/useHoox';
import {useSideEffects} from './sideEffects';
import {getCartQuoteSaga} from './store/actions';
import {getQuote} from './store/reducers/quoteItems/quoteItems';
import {
    getQuoteItems,
    hasCartContactLenses,
    isCartLoading,
    isFullLensesCart,
} from './store/selectors';
import {getSpecialOfferBanner} from './store/selectors/specialOffer/specialOffer';

const NewCartUI = loadable(
    () =>
        import(/* webpackChunkName: "CartContainer" */ './components/cartContainer/cartContainer'),
    {
        fallback: <CartSkeleton />,
        resolveComponent: components => components.CartContainer,
    },
);

type NativePropsType = {
    isCheckOrderRequestDone: boolean;
    isContactsInCart: boolean;
};

type StoreType = {
    itemsCount: number;
    specialOfferData: string;
    messages: Array<MessageType>;
    isLoading: boolean;
    cartUpsells: {status: string};
    coupons: {
        status: string;
        messages?: [];
        error?: {messages?: {type: string; message: string}[]};
    };
    changeItemQty: {status: string};
    isFullLenses: boolean;
};

type DispatchType = {
    getCartQuoteSaga: () => void;
    getQuote: ({ids}: {ids?: Array<string>}) => void;
};

export type PropsType = NativePropsType & StoreType & DispatchType;

/**
 * Default component for Cart page
 */
export const Cart: FC<PropsType> = props => {
    const {isLoading, messages} = props;

    /**
     * Adding product by sku if we have addSKU in query parameter
     */
    const {errors: deepLinkErrors, isLoaded: isLoadedDeepLink} = useCartDeepLink();
    useHoox();

    const infoMessages = useMemo(
        () => [...messages, ...deepLinkErrors],
        [messages, deepLinkErrors],
    );

    useSideEffects(props);

    // cache one step checkout availability request
    useIsOneStepAvailable();

    useFeatureWithAnalytics('dLensTintsEgCart', false, 'desktop');

    useEffect(() => {
        sessionStore.set(PURCHASE_STORAGE, 'Cart');
    }, []);

    return <NewCartUI infoMessages={infoMessages} showSkeleton={isLoading || !isLoadedDeepLink} />;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
export const mapStateToProps = (store: ApplicationStoreType) => ({
    cartUpsells: store.cartUpsells,
    changeItemQty: store.changeItemQty,
    coupons: store.coupons,
    isContactsInCart: hasCartContactLenses(store),
    isFullLenses: isFullLensesCart(store),
    isLoading: isCartLoading(store),
    itemsCount: getQuoteItems(store).length,
    messages: store.cart.messages,
    specialOfferData: getSpecialOfferBanner(store),
});

export const CartConnected = connect(mapStateToProps, {
    getCartQuoteSaga,
    getQuote,
})(Cart);
